import { useQuery } from '@apollo/client';
import { Button, ButtonGroup, Tooltip, Typography, useTheme } from '@material-ui/core';
import { Sort, SortByAlpha } from '@material-ui/icons';
import { Skeleton } from '@material-ui/lab';
import { sortBy } from 'lodash';
import React from 'react';

import { buildStackedHorizontalBarOption } from 'shared/common/charts/options';
import { JobStatus } from 'shared/models/job';

import { echarts, ReactEChartsCore } from '../common/charts/echarts-core';
import { usePortalTooltip } from '../common/charts/tooltip';
import { Duration, getDurationOptions } from '../common/duration';
import {
    buildStackedTooltipFormatter,
    clientActivityFields,
    defaultAggregateViewSelected,
    Field,
    FieldsSelected,
    funnelMetrics,
    getChartHeight,
    getFieldColors,
    getFieldLabel
} from '../common/metrics';
import { isoDate } from '../common/timestamp';
import { EFFORT_JOBS, EffortJob } from '../graphql/queries/jobs-effort';
import { ACTIVITY_METRICS, MetricsData } from '../graphql/queries/metrics';
import { useChartExport } from '../hooks/use-chart-export';
import { useLocalStorage } from '../hooks/use-local-storage';
import { internalClientIds as excludeClientIds, JobFilterTypes, jobTypesForFilter } from '../state';
import { ChartExportButtons } from './charts/chart-export-buttons';
import { DurationSelector } from './duration-selector';
import { JobEffortDialog } from './job-effort-dialog';
import { JobsTypeFilter } from './jobs-type-filter';
import { Legend } from './metrics/legend';
import { styles } from './metrics/styles';

// Inactive jobs with only a couple of straggler activities (e.g. late replies) are noise, not signal
const minInactiveJobActivity = 5;

const statusSuffix = (status: JobStatus) =>
    status === JobStatus.Paused ? ' (Paused)' : status === JobStatus.Archived ? ' (Archived)' : '';

const getJobLabel = (job: EffortJob) => `${job.client.name} — ${job.title}${statusSuffix(job.status)}`;

const getJobDetail = (job: EffortJob) => `AM: ${job.accountManager.name} · Assigned: ${job.assignee.name}`;

export const JobsEffort: React.FC = () => {
    const theme = useTheme();
    const durationPeriods = { days: 2, weeks: 2, months: 2, quarters: 4, years: 2 };
    const durationOpts = getDurationOptions(durationPeriods);
    const defaultOption = durationOpts.find((opt) => opt.label === 'This Week');
    const [storedDuration, setDuration] = useLocalStorage<Duration>('metrics-duration', defaultOption);
    // The shared metrics-duration key may hold All Time (picked on other metrics pages); it would
    // fetch every activity_metrics row since 1970, so fall back to the default range here
    const duration = storedDuration.period === 'All Time' ? defaultOption : storedDuration;
    const [jobTypes, setJobTypes] = useLocalStorage<JobFilterTypes>('metrics-job-types', 'placement-fees');
    const { data: jobsData } = useQuery<{ jobs: EffortJob[] }>(EFFORT_JOBS, {
        variables: {
            endDate: isoDate(duration.end),
            excludeClientIds,
            jobTypes: jobTypesForFilter.get(jobTypes),
            kinds: clientActivityFields,
            startDate: isoDate(duration.start)
        }
    });
    const { data } = useQuery<{ metrics: MetricsData[] }>(ACTIVITY_METRICS, {
        variables: {
            where: {
                date: { _gte: isoDate(duration.start), _lte: isoDate(duration.end) },
                job: {
                    clientId: { _nin: excludeClientIds },
                    jobType: { _in: jobTypesForFilter.get(jobTypes) }
                },
                kind: { _in: clientActivityFields }
            }
        }
    });
    const [selectedFields, setSelectedFields] = useLocalStorage<FieldsSelected>(
        'agg-metrics-fields',
        defaultAggregateViewSelected
    );
    const [hoveredField, setHoveredField] = React.useState<Field | undefined>(undefined);
    const [variant, setVariant] = useLocalStorage<'cost' | 'count'>('agg-metrics-variant', 'count');
    const [sort, setSort] = useLocalStorage<'alpha' | 'sum'>('job-effort-sort', 'sum');
    const [selectedJobId, setSelectedJobId] = React.useState<string | undefined>(undefined);
    const chartExport = useChartExport({ title: 'jobs-effort' });

    const fields = React.useMemo(() => clientActivityFields.filter((f) => selectedFields[f]), [selectedFields]);

    const chartModel = React.useMemo(() => {
        if (!jobsData || !data) {
            return undefined;
        }
        const rowsByJob = new Map<string, MetricsData[]>();
        for (const metric of data.metrics) {
            const existing = rowsByJob.get(metric.job.id);
            if (existing) {
                existing.push(metric);
            } else {
                rowsByJob.set(metric.job.id, [metric]);
            }
        }
        const entries = jobsData.jobs
            .filter((job) => {
                if (job.status === JobStatus.Active) {
                    return true;
                }
                const rawCount = (rowsByJob.get(job.id) ?? []).reduce((acc, row) => acc + row.count, 0);
                return rawCount >= minInactiveJobActivity;
            })
            .map((job) => {
                const rows = rowsByJob.get(job.id) ?? [];
                const jobMetrics = funnelMetrics(rows, variant);
                const sum = fields.reduce((acc, field) => acc + (jobMetrics[field] ?? 0), 0);
                return {
                    detail: getJobDetail(job),
                    job,
                    label: getJobLabel(job),
                    metrics: jobMetrics,
                    rows,
                    sum
                };
            });
        const sortedEntries =
            sort === 'alpha'
                ? sortBy(entries, (entry) => entry.label)
                : sortBy(entries, [(entry) => -entry.sum, (entry) => entry.label]);
        const categories = sortedEntries.map((entry) => entry.label);
        const series = fields.map((field) => ({
            data: sortedEntries.map((entry) => entry.metrics[field] ?? 0),
            name: getFieldLabel(field)
        }));
        return {
            categories,
            entries: sortedEntries,
            series
        };
    }, [data, fields, jobsData, sort, variant]);

    const tooltipFormatter = React.useMemo(() => {
        if (!chartModel) {
            return () => '';
        }
        return buildStackedTooltipFormatter({
            entries: chartModel.entries.map((entry) => ({ label: entry.label, metrics: entry.metrics })),
            fields,
            header: 'Job',
            series: chartModel.series
        });
    }, [chartModel, fields]);

    const tooltip = usePortalTooltip(
        {
            formatter: tooltipFormatter,
            trigger: 'item'
        },
        [tooltipFormatter]
    );

    const colors = React.useMemo(() => getFieldColors(fields, hoveredField), [fields, hoveredField]);

    const axisLabel = React.useMemo(() => {
        if (!chartModel) {
            return undefined;
        }
        const formatter = (value: string, index: number) => {
            const detail = chartModel.entries[index]?.detail;
            return detail ? `${value}\n{detail|${detail}}` : value;
        };
        return {
            formatter,
            overflow: 'truncate',
            rich: {
                detail: {
                    color: 'rgba(38, 50, 56, 0.55)',
                    fontSize: 10,
                    lineHeight: 14
                }
            },
            width: 240
        };
    }, [chartModel]);

    const chartOptions = React.useMemo(() => {
        if (!chartModel || !chartModel.categories.length || !axisLabel) {
            return undefined;
        }
        return buildStackedHorizontalBarOption({
            axisLabel,
            categories: chartModel.categories,
            colors,
            series: chartModel.series,
            tooltip
        });
    }, [axisLabel, chartModel, colors, tooltip]);

    const handleChartClick = React.useCallback(
        (params: any) => {
            const entry = chartModel?.entries[params?.dataIndex];
            if (entry) {
                setSelectedJobId(entry.job.id);
            }
        },
        [chartModel]
    );

    const chartEvents = React.useMemo(() => ({ click: handleChartClick }), [handleChartClick]);

    const hasChartData = Boolean(chartModel && chartModel.categories.length > 0 && chartOptions);

    const handleVariantChange = (value: 'cost' | 'count') => () => {
        setVariant(value);
    };

    const handleSortChange = (value: 'alpha' | 'sum') => () => {
        setSort(value);
    };

    const handleDialogClose = () => {
        setSelectedJobId(undefined);
    };

    const selectedEntry = selectedJobId
        ? chartModel?.entries.find((entry) => entry.job.id === selectedJobId)
        : undefined;
    const dialog = selectedEntry ? (
        <JobEffortDialog
            fields={fields}
            jobId={selectedEntry.job.id}
            jobLabel={selectedEntry.label}
            metrics={selectedEntry.rows}
            variant={variant}
            onClose={handleDialogClose}
        />
    ) : null;

    const chart =
        !data || !jobsData ? (
            <Skeleton variant="rect" />
        ) : hasChartData && chartOptions ? (
            <ReactEChartsCore
                ref={chartExport.chartRef}
                echarts={echarts}
                option={chartOptions}
                notMerge={true}
                lazyUpdate={true}
                onEvents={chartEvents}
                style={{ height: getChartHeight(chartModel?.categories.length ?? 0) }}
                opts={{ renderer: 'svg' }}
            />
        ) : null;

    const canExport = Boolean(data && jobsData && hasChartData);

    const tabs = (
        <div className="view-selectors">
            <ButtonGroup size="small" variant="text">
                <Tooltip title="Sort Alphabetical">
                    <Button className={sort === 'alpha' ? '' : 'inactive'} onClick={handleSortChange('alpha')}>
                        <SortByAlpha fontSize="small" />
                    </Button>
                </Tooltip>
                <Tooltip title="Sort by Activity">
                    <Button className={sort === 'sum' ? '' : 'inactive'} onClick={handleSortChange('sum')}>
                        <Sort fontSize="small" />
                    </Button>
                </Tooltip>
            </ButtonGroup>
            <ButtonGroup size="small" variant="text">
                <Tooltip title="Individual Action Counts">
                    <Button className={variant === 'count' ? '' : 'inactive'} onClick={handleVariantChange('count')}>
                        Raw Counts
                    </Button>
                </Tooltip>
                <Tooltip title="Weighted sums based on estimated action time">
                    <Button className={variant === 'cost' ? '' : 'inactive'} onClick={handleVariantChange('cost')}>
                        Weighted Sums
                    </Button>
                </Tooltip>
            </ButtonGroup>
        </div>
    );

    const content = (
        <div className="chart-container">
            <div className="panel-header">
                <div className="panel-title">
                    <Typography variant="h5">Job Effort</Typography>
                </div>
                <div className="panel-options">
                    {tabs}
                    <ChartExportButtons
                        disabled={!canExport}
                        isExporting={chartExport.isExporting}
                        onExportPng={chartExport.exportAsPng}
                        onExportSvg={chartExport.exportAsSvg}
                    />
                </div>
            </div>
            <div className="panel-content">
                <div className="chart">{chart}</div>
                <Legend
                    fields={clientActivityFields}
                    selected={selectedFields}
                    onFieldHover={setHoveredField}
                    onUpdateSelected={setSelectedFields}
                />
            </div>
        </div>
    );

    return (
        <div css={styles(theme)}>
            <div className="metrics-container">
                <div className="selectors">
                    <DurationSelector selected={duration} onSelect={setDuration} periods={durationPeriods} />
                    <JobsTypeFilter selectedFilter={jobTypes} onSelectFilter={setJobTypes} />
                </div>
                {content}
                {dialog}
            </div>
        </div>
    );
};
